import clsx from "clsx";
import { useMemo, useState } from "react";

import { KEYS } from "@excalidraw/common";

import {
  NO_SCENE_CONTEXT_COLOR,
  NO_SCENE_CONTEXT_ID,
  SCENE_CONTEXT_COLORS,
  getSceneContextElementCounts,
  getUnknownSceneContextIds,
} from "@excalidraw/element";

import {
  actionAddSceneContext,
  actionChangeSceneContextColor,
  actionDeleteSceneContext,
  actionRenameSceneContext,
  actionSetActiveSceneContext,
  actionToggleElementTimestamps,
  actionToggleSceneContextMode,
  actionToggleSceneContextVisibility,
  getSceneContextDisplayName,
} from "../actions/actionSceneContexts";
import { useUIAppState } from "../context/ui-appState";
import { t } from "../i18n";

import { useExcalidrawActionManager, useExcalidrawElements } from "./App";
import {
  DotsHorizontalIcon,
  eyeClosedIcon,
  eyeIcon,
  PlusIcon,
  TrashIcon,
} from "./icons";

import "./SceneContextsMenu.scss";

type SceneContextRow = {
  /** visibility key: context id or `NO_SCENE_CONTEXT_ID` */
  key: string;
  name: string;
  color: string;
  kind: "registry" | "unknown" | "none";
};

const SceneContextRowItem = ({
  row,
  count,
  isActive,
  isHidden,
  onActivate,
  onToggleVisibility,
  onRename,
  onColor,
  onDelete,
}: {
  row: SceneContextRow;
  count: number;
  isActive: boolean;
  isHidden: boolean;
  onActivate: (() => void) | null;
  onToggleVisibility: (solo: boolean) => void;
  onRename: ((name: string) => void) | null;
  onColor: ((color: string) => void) | null;
  onDelete: (() => void) | null;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(row.name);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const startEditing = () => {
    if (!onRename) {
      return;
    }
    setDraftName(row.kind === "unknown" ? "" : row.name);
    setIsEditing(true);
    setIsMenuOpen(false);
  };

  const commitEditing = () => {
    setIsEditing(false);
    if (onRename && draftName.trim() && draftName.trim() !== row.name) {
      onRename(draftName.trim());
    }
  };

  const hasMenu = !!(onRename || onColor || onDelete);

  return (
    <li
      className={clsx("scene-contexts__row", {
        "scene-contexts__row--active": isActive,
        "scene-contexts__row--hidden": isHidden,
        "scene-contexts__row--unknown": row.kind === "unknown",
      })}
      data-testid={`scene-context-row-${row.key}`}
    >
      <div className="scene-contexts__row-main">
        <span
          className="scene-contexts__swatch"
          style={{ backgroundColor: row.color }}
          aria-hidden="true"
        />
        {isEditing ? (
          <input
            className="scene-contexts__name-input"
            aria-label={t("sceneContexts.nameLabel")}
            autoFocus
            value={draftName}
            placeholder={row.name}
            onChange={(event) => setDraftName(event.target.value)}
            onBlur={commitEditing}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === KEYS.ENTER) {
                commitEditing();
              } else if (event.key === KEYS.ESCAPE) {
                setIsEditing(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="scene-contexts__name"
            title={onActivate ? t("sceneContexts.activeHint") : undefined}
            aria-pressed={isActive}
            onClick={() => onActivate?.()}
            onDoubleClick={startEditing}
          >
            <span className="scene-contexts__name-text">{row.name}</span>
            {isActive && (
              <span className="scene-contexts__active-badge">
                {t("sceneContexts.active")}
              </span>
            )}
          </button>
        )}
        <span
          className="scene-contexts__count"
          title={t("sceneContexts.elementCount", { count })}
        >
          {count}
        </span>
        <button
          type="button"
          className="scene-contexts__icon-button"
          data-testid={`scene-context-eye-${row.key}`}
          aria-pressed={!isHidden}
          aria-label={
            isHidden ? t("sceneContexts.show") : t("sceneContexts.hide")
          }
          title={`${
            isHidden ? t("sceneContexts.show") : t("sceneContexts.hide")
          } — ${t("sceneContexts.visibilityHint")}`}
          onClick={(event) => onToggleVisibility(event.altKey)}
        >
          {isHidden ? eyeClosedIcon : eyeIcon}
        </button>
        {hasMenu ? (
          <button
            type="button"
            className="scene-contexts__icon-button"
            aria-label={t("sceneContexts.menu")}
            aria-expanded={isMenuOpen}
            title={t("sceneContexts.menu")}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            {DotsHorizontalIcon}
          </button>
        ) : (
          <span className="scene-contexts__icon-placeholder" />
        )}
      </div>
      {isMenuOpen && (
        <div className="scene-contexts__row-menu">
          {onRename && (
            <button
              type="button"
              className="scene-contexts__menu-button"
              onClick={startEditing}
            >
              {t("sceneContexts.rename")}
            </button>
          )}
          {onColor && (
            <div
              className="scene-contexts__colors"
              role="group"
              aria-label={t("sceneContexts.changeColor")}
            >
              {SCENE_CONTEXT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={clsx("scene-contexts__color", {
                    "scene-contexts__color--selected": color === row.color,
                  })}
                  style={{ backgroundColor: color }}
                  aria-label={color}
                  title={color}
                  onClick={() => onColor(color)}
                />
              ))}
            </div>
          )}
          {onDelete && (
            <button
              type="button"
              className="scene-contexts__menu-button scene-contexts__menu-button--danger"
              onClick={() => {
                setIsMenuOpen(false);
                onDelete();
              }}
            >
              {TrashIcon}
              {t("sceneContexts.delete")}
            </button>
          )}
        </div>
      )}
    </li>
  );
};

export const SceneContextsMenu = () => {
  const appState = useUIAppState();
  const elements = useExcalidrawElements();
  const actionManager = useExcalidrawActionManager();

  const {
    sceneContexts,
    activeSceneContextId,
    hiddenSceneContextIds,
    sceneContextModeEnabled,
    showElementTimestamps,
  } = appState;

  const counts = useMemo(
    () => getSceneContextElementCounts(elements),
    [elements],
  );

  const rows = useMemo((): SceneContextRow[] => {
    const unknownIds = getUnknownSceneContextIds(elements, sceneContexts);
    return [
      ...sceneContexts.map(
        (sceneContext): SceneContextRow => ({
          key: sceneContext.id,
          name: getSceneContextDisplayName(sceneContext.id, sceneContexts),
          color: sceneContext.color,
          kind: "registry",
        }),
      ),
      ...unknownIds.map(
        (id): SceneContextRow => ({
          key: id,
          name: `${t("sceneContexts.unknownContext")} (${id.slice(0, 6)})`,
          color: NO_SCENE_CONTEXT_COLOR,
          kind: "unknown",
        }),
      ),
      {
        key: NO_SCENE_CONTEXT_ID,
        name: t("sceneContexts.noContext"),
        color: NO_SCENE_CONTEXT_COLOR,
        kind: "none",
      },
    ];
  }, [elements, sceneContexts]);

  return (
    <div className="scene-contexts" data-testid="scene-contexts-menu">
      <div className="scene-contexts__header">
        <div className="scene-contexts__title">{t("sceneContexts.title")}</div>
        <label
          className="scene-contexts__toggle"
          title={t("sceneContexts.modeHint")}
        >
          <input
            type="checkbox"
            data-testid="scene-context-mode-toggle"
            checked={sceneContextModeEnabled}
            onChange={() =>
              actionManager.executeAction(actionToggleSceneContextMode, "ui")
            }
          />
          {t("sceneContexts.mode")}
        </label>
        <label
          className="scene-contexts__toggle"
          title={t("sceneContexts.timestampsHint")}
        >
          <input
            type="checkbox"
            data-testid="scene-context-timestamps-toggle"
            checked={showElementTimestamps}
            onChange={() =>
              actionManager.executeAction(actionToggleElementTimestamps, "ui")
            }
          />
          {t("sceneContexts.timestamps")}
        </label>
        <button
          type="button"
          className="scene-contexts__add"
          data-testid="scene-context-add"
          onClick={() =>
            actionManager.executeAction(actionAddSceneContext, "ui", {})
          }
        >
          {PlusIcon}
          {t("sceneContexts.add")}
        </button>
      </div>
      {!sceneContexts.length && (
        <div className="scene-contexts__empty">{t("sceneContexts.empty")}</div>
      )}
      <ul className="scene-contexts__list">
        {rows.map((row) => {
          const isActive =
            row.kind === "none"
              ? sceneContextModeEnabled && !activeSceneContextId
              : row.key === activeSceneContextId;
          return (
            <SceneContextRowItem
              key={row.key}
              row={row}
              count={counts.get(row.key) ?? 0}
              isActive={isActive}
              isHidden={!!hiddenSceneContextIds[row.key]}
              onActivate={
                row.kind === "unknown"
                  ? null
                  : () =>
                      actionManager.executeAction(
                        actionSetActiveSceneContext,
                        "ui",
                        { id: row.key },
                      )
              }
              onToggleVisibility={(solo) =>
                actionManager.executeAction(
                  actionToggleSceneContextVisibility,
                  "ui",
                  { key: row.key, solo },
                )
              }
              onRename={
                row.kind === "none"
                  ? null
                  : (name) =>
                      actionManager.executeAction(
                        actionRenameSceneContext,
                        "ui",
                        { id: row.key, name },
                      )
              }
              onColor={
                row.kind === "none"
                  ? null
                  : (color) =>
                      actionManager.executeAction(
                        actionChangeSceneContextColor,
                        "ui",
                        { id: row.key, color },
                      )
              }
              onDelete={
                row.kind === "registry"
                  ? () =>
                      actionManager.executeAction(
                        actionDeleteSceneContext,
                        "ui",
                        { id: row.key, confirm: true },
                      )
                  : null
              }
            />
          );
        })}
      </ul>
    </div>
  );
};
